import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod/v4";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DIALOG_FORM_CLASSES,
  DialogBody,
} from "@/components/ui/dialog";
import { useCloseGuard } from "@/hooks/use-close-guard";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  COMPONENT_TYPES,
  type ComponentType,
  type PricingComponentDetailDto,
} from "@/types/pricing-component";
import {
  useCreatePricingComponent,
  useUpdatePricingComponent,
} from "@/hooks/usePricingComponents";

// ============================================================================
// Validation Schema
// ============================================================================

const formSchema = z.object({
  name: z
    .string()
    .min(3, "Name must be at least 3 characters")
    .max(100, "Name must be less than 100 characters"),
  slug: z
    .string()
    .optional()
    .refine(
      (val) => !val || /^[a-z0-9-]+$/.test(val),
      "Slug must be lowercase alphanumeric with hyphens only",
    ),
  unit: z
    .string()
    .min(1, "Unit is required")
    .max(20, "Unit must be less than 20 characters"),
  componentType: z.enum(COMPONENT_TYPES),
  description: z.string().optional(),
  isActive: z.boolean().default(true),
  metadata: z
    .string()
    .optional()
    .refine(
      (val) =>
        !val ||
        val.trim() === "" ||
        (() => {
          try {
            JSON.parse(val);
            return true;
          } catch {
            return false;
          }
        })(),
      "Metadata must be valid JSON",
    ),
});

type FormValues = z.infer<typeof formSchema>;

// ============================================================================
// Component Props
// ============================================================================

interface PricingComponentFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  component?: PricingComponentDetailDto;
  mode: "create" | "edit";
}

// ============================================================================
// Component
// ============================================================================

export function PricingComponentForm({
  open,
  onOpenChange,
  component,
  mode,
}: PricingComponentFormProps) {
  const createMutation = useCreatePricingComponent();
  const updateMutation = useUpdatePricingComponent();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      slug: "",
      unit: "",
      componentType: "Resource" as ComponentType,
      description: "",
      isActive: true,
      metadata: "",
    },
    values: component
      ? {
          name: component.name,
          slug: component.slug,
          unit: component.unit,
          componentType: component.componentType,
          description: component.description || "",
          isActive: component.isActive,
          metadata: component.metadata || "",
        }
      : undefined,
  });

  // Auto-generate slug from name in create mode
  const watchName = form.watch("name");
  useEffect(() => {
    if (mode === "create" && watchName && !form.formState.dirtyFields.slug) {
      const slug = watchName
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "");
      form.setValue("slug", slug, { shouldDirty: false });
    }
  }, [watchName, mode, form]);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open, form]);

  const onSubmit = async (data: FormValues) => {
    try {
      if (mode === "create") {
        await createMutation.mutateAsync({
          name: data.name,
          slug: data.slug,
          unit: data.unit,
          componentType: data.componentType,
          description: data.description,
          metadata: data.metadata,
        });
      } else if (component) {
        await updateMutation.mutateAsync({
          id: component.id,
          data: {
            name: data.name,
            unit: data.unit,
            description: data.description,
            isActive: data.isActive,
            metadata: data.metadata,
          },
        });
      }
      onOpenChange(false);
      form.reset();
    } catch (error) {
      // Error handling is done in the mutation hooks via toast
      console.error("Form submission error:", error);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const guard = useCloseGuard({
    open,
    onOpenChange,
    isDirty: form.formState.isDirty,
    isSubmitting,
    onDiscard: () => form.reset(),
  });

  return (
    <>
      <Dialog open={open} onOpenChange={guard.onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {mode === "create"
                ? "Create Pricing Component"
                : "Edit Pricing Component"}
            </DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className={DIALOG_FORM_CLASSES}
            >
              <DialogBody className="space-y-6">
                <DialogDescription>
                  {mode === "create"
                    ? "Create a new pricing component building block for your products."
                    : "Update the pricing component details."}
                </DialogDescription>
                {/* Name Field */}
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g., CPU Core, Storage GB, Bandwidth"
                          {...field}
                          disabled={isSubmitting}
                        />
                      </FormControl>
                      <FormDescription>
                        A descriptive name for the pricing component
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Slug Field */}
                <FormField
                  control={form.control}
                  name="slug"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Slug</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g., cpu-core, storage-gb"
                          {...field}
                          disabled={isSubmitting || mode === "edit"}
                        />
                      </FormControl>
                      <FormDescription>
                        {mode === "create"
                          ? "URL-friendly identifier (auto-generated from name)"
                          : "Slug cannot be changed after creation"}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Unit Field */}
                <FormField
                  control={form.control}
                  name="unit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Unit *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g., core, GB, TB, hours, users"
                          {...field}
                          disabled={isSubmitting}
                        />
                      </FormControl>
                      <FormDescription>
                        Unit of measurement for this component
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Component Type Field */}
                <FormField
                  control={form.control}
                  name="componentType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Component Type *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        disabled={isSubmitting || mode === "edit"}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a component type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {COMPONENT_TYPES.map((ct) => (
                            <SelectItem key={ct} value={ct}>
                              {ct}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        {mode === "create"
                          ? "Category of this pricing component"
                          : "Component type cannot be changed after creation"}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Description Field */}
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Optional description of the component..."
                          className="resize-none"
                          rows={3}
                          {...field}
                          disabled={isSubmitting}
                        />
                      </FormControl>
                      <FormDescription>
                        Additional details about this pricing component
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Status Toggle (Edit mode only) */}
                {mode === "edit" && (
                  <FormField
                    control={form.control}
                    name="isActive"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">
                            Active Status
                          </FormLabel>
                          <FormDescription>
                            Inactive components cannot be used in new pricing
                            rules
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            disabled={isSubmitting}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                )}

                {/* Metadata Field (Optional) */}
                <FormField
                  control={form.control}
                  name="metadata"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Metadata (JSON)</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder='{"key": "value"}'
                          className="resize-none font-mono text-sm"
                          rows={3}
                          {...field}
                          disabled={isSubmitting}
                        />
                      </FormControl>
                      <FormDescription>
                        Optional JSON metadata for custom properties
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Form Actions */}
              </DialogBody>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => guard.onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting
                    ? mode === "create"
                      ? "Creating..."
                      : "Updating..."
                    : mode === "create"
                      ? "Create Component"
                      : "Update Component"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      {guard.dialog}
    </>
  );
}
